/**
 * NicknameManager — assign + manage private nicknames for other Orchists.
 *
 * Phase G.7.7 (sacred/nicknames.md):
 *   "Recognition is what The Line records publicly. Familiarity is what
 *    the Orchist holds privately. Both are real moments in time; only
 *    the public is shared."
 *
 * Surface:
 *   - Top: existing nicknames (paginated; search filters by nickname/notes)
 *   - Bottom: "+ Assign new" expandable section with a paginated picker of
 *     Orchists in the lineage (excluding self + already-nicknamed)
 *
 * Privacy: RLS limits SELECT on OrchistNickname to the row owner.
 * The public LineageList query never sees these rows; the nickname
 * overlay is rendered client-side for the viewing Orchist only.
 *
 * @scale-marker stage-a — at ~1k Orchists in the picker, virtualize.
 *   useLineage already paginates; the picker just renders that.
 * @scale-marker stage-b — at ~100k, full-text search via Postgres on
 *   target's publicIdentityString; current MVP filters client-side.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNicknames, type NicknameRow } from '@/hooks/useNicknames';
import { useLineage, lineageDisplayString } from '@/hooks/useLineage';
import { usePendingInvitees } from '@/hooks/usePendingInvitees';
import { Glyph } from '@/components/Glyph';
import { resolveTimezone, utcToLocalDateString } from '@/lib/dates';
import {
  OrchistSearchInput,
  matchesOrchistQuery,
} from '@/components/OrchistSearch';

export interface NicknameManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NicknameManager({ isOpen, onClose }: NicknameManagerProps) {
  const { contact } = useAuth();
  const tz = resolveTimezone(contact?.timezone);

  const {
    rows: nicknames,
    byTargetId,
    totalCount: nicknameTotal,
    hasMore: nicknamesHasMore,
    loadMore: loadMoreNicknames,
    loadingMore: loadingMoreNicknames,
    loading: nicknamesLoading,
    setNickname,
    clearNickname,
  } = useNicknames();

  const {
    rows: lineageRows,
    totalCount: lineageTotal,
    hasMore: lineageHasMore,
    loadMore: loadMoreLineage,
    loadingMore: loadingMoreLineage,
  } = useLineage();

  // Pending invitees the caller has invited who haven't yet Acknowledged.
  // We surface them here so a nickname assigned at invite-time doesn't
  // render as "(loading…)" or "(unknown Orchist)" — TAD honesty: the
  // gesture exists, the surface should reflect it.
  const { rows: pendingInvitees } = usePendingInvitees();
  const pendingByContactId = useMemo(() => {
    const m = new Map<string, (typeof pendingInvitees)[number]>();
    for (const p of pendingInvitees) m.set(p.id, p);
    return m;
  }, [pendingInvitees]);

  const [searchQuery, setSearchQuery] = useState('');
  const [editingTargetId, setEditingTargetId] = useState<string | null>(null);
  const [draftNickname, setDraftNickname] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (editingTargetId) {
          setEditingTargetId(null);
        } else {
          onClose();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, editingTargetId]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setEditingTargetId(null);
      setDraftNickname('');
      setDraftNotes('');
      setSavingState('idle');
      setErrorMessage(null);
      setPickerOpen(false);
    }
  }, [isOpen]);

  // Map target Orchist info for inline display (so we can show their public
  // identity alongside the private nickname).
  const targetById = useMemo(() => {
    const map = new Map<string, (typeof lineageRows)[number]>();
    for (const r of lineageRows) map.set(r.id, r);
    return map;
  }, [lineageRows]);

  // FAB filter: Fuzzy + Boolean engine across nickname, notes, public name,
  // and pending-invitee email when no lineage row exists yet.
  const filteredNicknames = useMemo(() => {
    if (!searchQuery.trim()) return nicknames;
    const q = searchQuery.trim().toLowerCase();
    return nicknames.filter((n) => {
      const target = targetById.get(n.targetContactId);
      if (target) {
        return matchesOrchistQuery(target, n, searchQuery, tz);
      }
      // No lineage row — could be pending invitee or just unloaded.
      const pending = pendingByContactId.get(n.targetContactId);
      const email = pending?.email ?? '';
      return (
        n.nickname.toLowerCase().includes(q) ||
        (n.notes ?? '').toLowerCase().includes(q) ||
        email.toLowerCase().includes(q)
      );
    });
  }, [nicknames, searchQuery, targetById, pendingByContactId, tz]);

  // Pickable Orchists for "+ Assign new": lineage rows excluding self +
  // already-nicknamed. FAB-filtered by search if open.
  const pickerCandidates = useMemo(() => {
    if (!pickerOpen) return [];
    return lineageRows.filter((r) => {
      if (r.id === contact?.id) return false;
      if (byTargetId.has(r.id)) return false;
      // No nickname for picker candidates by definition; pass null
      return matchesOrchistQuery(r, null, searchQuery, tz);
    });
  }, [lineageRows, byTargetId, pickerOpen, searchQuery, contact, tz]);

  if (!isOpen) return null;

  function startEdit(targetId: string, existing?: NicknameRow) {
    setEditingTargetId(targetId);
    setDraftNickname(existing?.nickname ?? '');
    setDraftNotes(existing?.notes ?? '');
    setErrorMessage(null);
  }

  async function handleSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (savingState === 'saving' || !editingTargetId) return;
    const cleaned = draftNickname.trim();
    if (!cleaned) {
      setErrorMessage('Nickname cannot be empty.');
      return;
    }
    setSavingState('saving');
    setErrorMessage(null);
    try {
      await setNickname(editingTargetId, cleaned, draftNotes.trim() || null);
      setSavingState('idle');
      setEditingTargetId(null);
      setDraftNickname('');
      setDraftNotes('');
      setPickerOpen(false);
    } catch (err) {
      setSavingState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Save failed.');
    }
  }

  async function handleClear(targetId: string) {
    if (savingState === 'saving') return;
    setSavingState('saving');
    setErrorMessage(null);
    try {
      await clearNickname(targetId);
      setSavingState('idle');
      if (editingTargetId === targetId) setEditingTargetId(null);
    } catch (err) {
      setSavingState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Clear failed.');
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center px-6 py-12">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-paper/80 backdrop-blur-sm cursor-default"
      />

      <div className="relative w-full max-w-xl bg-paper border border-ink/20 shadow-xl p-8 space-y-6 max-h-[90vh] overflow-y-auto">
        <div className="text-center space-y-2">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            Familiarity, held privately
          </p>
          <h2 className="display text-3xl font-medium leading-tight">
            <span className="italic text-accent">Private nicknames.</span>
          </h2>
          <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto leading-relaxed">
            Recognition is what The Line records publicly. Familiarity is what
            you hold privately. The Line never shows your nicknames to anyone
            else &mdash; not even the Orchists you've named.
          </p>
        </div>

        {/* FAB search */}
        <OrchistSearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by nickname, note, or public identity..."
          summary={
            searchQuery
              ? `${filteredNicknames.length} of ${nicknames.length} loaded match`
              : undefined
          }
        />

        {/* Existing nicknames */}
        <section className="space-y-3">
          <p className="text-xs tracking-wide uppercase text-ink/60">
            {(nicknameTotal ?? 0) === 0
              ? 'You have no nicknames yet'
              : `Your nicknames (${nicknameTotal})`}
          </p>

          {nicknamesLoading ? (
            <p className="text-sm italic text-ink/40 font-display">Reading...</p>
          ) : filteredNicknames.length === 0 ? (
            <p className="text-sm italic text-ink/40 font-display">
              {searchQuery ? 'No matches.' : 'No nicknames assigned.'}
            </p>
          ) : (
            <ul className="space-y-3 list-none pl-0">
              {filteredNicknames.map((n) => {
                const target = targetById.get(n.targetContactId);
                const pending = pendingByContactId.get(n.targetContactId);
                const isPending = !target && !!pending;
                const publicDisplay = target
                  ? lineageDisplayString(target, tz)
                  : pending
                    ? pending.email
                    : '(loading…)';
                const isEditing = editingTargetId === n.targetContactId;

                return (
                  <li key={n.id} className="border border-ink/15 px-4 py-3 space-y-2">
                    <div className="flex items-start gap-3">
                      {target?.glyphSeed && (
                        <div className="flex-shrink-0 text-ink/75 mt-0.5">
                          <Glyph
                            seed={target.glyphSeed}
                            size={32}
                            label={`${publicDisplay}'s sigil`}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-base truncate ${isPending ? 'font-mono text-sm' : 'display'}`}>
                          {publicDisplay}
                        </p>
                        {isPending && (
                          <p className="text-[0.65rem] tracking-[0.2em] uppercase text-ink/40 mt-0.5">
                            pending acknowledgment
                          </p>
                        )}
                        {!isEditing && (
                          <>
                            <p className="text-xs text-accent italic font-display mt-0.5">
                              you call them: <span className="text-ink">{n.nickname}</span>
                            </p>
                            {n.notes && (
                              <p className="text-xs text-ink/55 italic font-display mt-1">
                                {n.notes}
                              </p>
                            )}
                            <p className="text-[0.65rem] text-ink/35 italic mt-1">
                              Updated {utcToLocalDateString(n.updatedAt, tz) ?? '—'}
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    {!isEditing ? (
                      <div className="flex gap-3 text-xs pt-1">
                        <button
                          type="button"
                          onClick={() => startEdit(n.targetContactId, n)}
                          className="italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 text-ink/65 hover:text-ink transition-colors"
                        >
                          Edit
                        </button>
                        <span className="text-ink/25">·</span>
                        <button
                          type="button"
                          onClick={() => handleClear(n.targetContactId)}
                          disabled={savingState === 'saving'}
                          className="italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 text-ink/55 hover:text-ink transition-colors disabled:opacity-40"
                        >
                          Clear
                        </button>
                      </div>
                    ) : (
                      <EditForm
                        nickname={draftNickname}
                        notes={draftNotes}
                        onNicknameChange={setDraftNickname}
                        onNotesChange={setDraftNotes}
                        onSave={handleSave}
                        onCancel={() => setEditingTargetId(null)}
                        saving={savingState === 'saving'}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {nicknamesHasMore && (
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => void loadMoreNicknames()}
                disabled={loadingMoreNicknames}
                className="text-xs italic text-ink/55 underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 hover:text-ink transition-colors disabled:opacity-50"
              >
                {loadingMoreNicknames
                  ? 'Loading more...'
                  : `Load more (${(nicknameTotal ?? 0) - nicknames.length} remaining)`}
              </button>
            </div>
          )}
        </section>

        {/* Add new — picker */}
        <section className="border-t border-ink/10 pt-6">
          <button
            type="button"
            onClick={() => {
              setPickerOpen((v) => !v);
              setEditingTargetId(null);
            }}
            className="w-full text-left flex items-center justify-between text-sm tracking-wide uppercase text-ink/60 hover:text-ink/80 transition-colors"
          >
            <span>+ Assign new nickname</span>
            <span className="text-[0.65rem]">{pickerOpen ? '— hide' : '+ show'}</span>
          </button>

          {pickerOpen && (
            <div className="mt-4 space-y-3">
              <p className="text-xs italic font-display text-ink/45 leading-relaxed">
                Any Orchist in The Line can be nicknamed by you. Use the search
                above to filter; click a row to assign a name only you will see.
              </p>
              {pickerCandidates.length === 0 ? (
                <p className="text-xs italic text-ink/40 font-display">
                  {lineageRows.length <= 1
                    ? "You're alone in The Line for now."
                    : 'No matching Orchists.'}
                </p>
              ) : (
                <ul className="space-y-2 list-none pl-0">
                  {pickerCandidates.map((r) => {
                    const display = lineageDisplayString(r, tz);
                    const isEditing = editingTargetId === r.id;
                    return (
                      <li key={r.id} className="border border-ink/10 px-3 py-2">
                        <div className="flex items-center gap-3">
                          {r.glyphSeed && (
                            <div className="flex-shrink-0 text-ink/70">
                              <Glyph
                                seed={r.glyphSeed}
                                size={24}
                                label={`${display}'s sigil`}
                              />
                            </div>
                          )}
                          <p className="display text-sm flex-1 truncate">{display}</p>
                          {!isEditing && (
                            <button
                              type="button"
                              onClick={() => startEdit(r.id)}
                              className="text-xs italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 text-ink/65 hover:text-ink transition-colors flex-shrink-0"
                            >
                              + Nickname
                            </button>
                          )}
                        </div>
                        {isEditing && (
                          <div className="mt-3">
                            <EditForm
                              nickname={draftNickname}
                              notes={draftNotes}
                              onNicknameChange={setDraftNickname}
                              onNotesChange={setDraftNotes}
                              onSave={handleSave}
                              onCancel={() => setEditingTargetId(null)}
                              saving={savingState === 'saving'}
                            />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {lineageHasMore && (
                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => void loadMoreLineage()}
                    disabled={loadingMoreLineage}
                    className="text-xs italic text-ink/55 underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 hover:text-ink transition-colors disabled:opacity-50"
                  >
                    {loadingMoreLineage
                      ? 'Loading more...'
                      : `Load more Orchists (${(lineageTotal ?? 0) - lineageRows.length} remaining)`}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        {errorMessage && (
          <p className="text-sm text-destructive text-center" role="alert">
            {errorMessage}
          </p>
        )}

        <button
          type="button"
          onClick={onClose}
          className="w-full px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function EditForm({
  nickname,
  notes,
  onNicknameChange,
  onNotesChange,
  onSave,
  onCancel,
  saving,
}: {
  nickname: string;
  notes: string;
  onNicknameChange: (s: string) => void;
  onNotesChange: (s: string) => void;
  onSave: (e: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <form onSubmit={onSave} className="space-y-2">
      <input
        type="text"
        value={nickname}
        onChange={(e) => onNicknameChange(e.target.value)}
        disabled={saving}
        autoFocus
        maxLength={120}
        placeholder="A name only you will see"
        className="w-full px-3 py-2 border border-ink/20 bg-paper/50 text-sm font-display placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50"
      />
      <textarea
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        disabled={saving}
        maxLength={500}
        rows={2}
        placeholder="Private notes (optional)"
        className="w-full px-3 py-2 border border-ink/20 bg-paper/50 text-xs font-display italic placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50 resize-none"
      />
      <div className="flex gap-2 text-xs">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 border border-ink/20 text-ink/65 italic font-display hover:border-ink/50 hover:text-ink transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-3 py-1.5 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}
